import { useState, useCallback, useRef, useEffect, useMemo } from 'react'
import { debounce } from 'es-toolkit'
import type { ScrollSpyNavItem, ScrollSpyNavProps } from './types.js'
import { cn } from '../../../../../helpers/index.js'
import { useScrollSpy } from './useScrollSpy.js'

const ScrollSpyNav = ({ tabs, onSpyNavItemClick, className, renderRating }: ScrollSpyNavProps) => {
  // 使用 useScrollSpy hook 自动监听滚动
  const [purchaseBarHeight, setPurchaseBarHeight] = useState(0)
  const combinedTabs = useMemo(() => {
    if (renderRating) {
      return [...tabs, { label: 'Reviews', id: 'ipc-review', href: '' }]
    }
    return tabs
  }, [tabs, renderRating])

  const sectionIds = useMemo(() => {
    return combinedTabs.map(tab => tab.id)
  }, [combinedTabs])

  const debouncedHandleResize = debounce(() => {
    const purchaseBar = document.querySelector('#purchase-bar')
    if (purchaseBar) {
      setPurchaseBarHeight(purchaseBar.getBoundingClientRect().height)
    }
  }, 500)

  useEffect(() => {
    debouncedHandleResize()
    window.addEventListener('resize', debouncedHandleResize)
    return () => {
      window.removeEventListener('resize', debouncedHandleResize)
    }
  }, [debouncedHandleResize])

  const activeId = useScrollSpy(sectionIds, purchaseBarHeight)

  // 移动端：横向滚动容器和 tab 按钮的 refs
  const scrollContainerRef = useRef<HTMLDivElement>(null)
  const tabRefs = useRef<Map<string, HTMLButtonElement>>(new Map())

  const onTabClick = useCallback(
    (tab: ScrollSpyNavItem) => {
      onSpyNavItemClick?.(tab)
    },
    [onSpyNavItemClick]
  )

  // 移动端：当 activeTab 改变时，自动滚动到中心位置
  useEffect(() => {
    if (!activeId || !scrollContainerRef.current) return

    const activeTabElement = tabRefs.current.get(activeId)
    if (!activeTabElement) return

    const container = scrollContainerRef.current
    const tabRect = activeTabElement.getBoundingClientRect()
    const containerRect = container.getBoundingClientRect()

    // 计算需要滚动的距离，使 tab 位于容器中心
    const tabCenter = tabRect.left + tabRect.width / 2 - containerRect.left
    const containerCenter = containerRect.width / 2
    const scrollOffset = tabCenter - containerCenter

    container.scrollTo({
      left: container.scrollLeft + scrollOffset,
      behavior: 'smooth',
    })
  }, [activeId])

  // 设置 tab ref
  const setTabRef = useCallback((tabId: string, element: HTMLButtonElement | null) => {
    if (element) {
      tabRefs.current.set(tabId, element)
    } else {
      tabRefs.current.delete(tabId)
    }
  }, [])

  return (
    <div
      ref={scrollContainerRef}
      style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
      className={cn('overflow-x-auto', className)}
    >
      <div className="tablet:gap-8 flex gap-6">
        {combinedTabs?.map(tab => (
          <button key={tab.id} ref={el => setTabRef(tab.id, el)}>
            {tab.id === 'ipc-review' ? (
              <div className="relative shrink-0 whitespace-nowrap py-[10px]">
                {renderRating}
                <div
                  className={cn(
                    'laptop-md:block bg-brand-0 absolute bottom-0 left-0 hidden h-[2px] w-0 transition-all duration-300 ease-in-out',
                    {
                      'w-full': activeId === tab.id,
                    }
                  )}
                />
              </div>
            ) : (
              <div
                className={cn(
                  'laptop-md:text-[#1d1d1f] relative shrink-0 whitespace-nowrap py-[10px] text-sm font-bold text-[#949494]',
                  {
                    'text-[#1d1d1f]': activeId === tab.id,
                  }
                )}
                onClick={() => onTabClick(tab)}
              >
                {tab.label}
                <div
                  className={cn(
                    'bg-brand-0 absolute bottom-0 left-0 h-[2px] w-0 transition-all duration-300 ease-in-out',
                    {
                      'w-full': activeId === tab.id,
                    }
                  )}
                />
              </div>
            )}
          </button>
        ))}
      </div>
    </div>
  )
}

export default ScrollSpyNav
